import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

const initialState = {
  isAuthenticated: false,
  token: null,
  user: null,
  loader: false,
  biometricLoader: false,
  language: "English",
  biometricSupported: false,
  biometricEnabled: false,
  firstTimeLogin: false,
  error: null,
  tokenExpired: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Regular login flow
    login(state) {
      state.loader = true;
      state.error = null;
    },
    loginSuccess(state, action) {
      state.loader = false;
      state.isAuthenticated = !!action.payload.token;
      state.token = JSON.stringify(action.payload.token);
      state.firstTimeLogin = action.payload.firstTimeLogin || false;
      state.tokenExpired = false;

      // Store in localStorage
      localStorage.setItem("isAuthenticated", state.isAuthenticated);
      localStorage.setItem("token", state.token);
      localStorage.setItem("token_bearer", action.payload.token);
      localStorage.setItem("token_type", action.payload.token_Type || "Bearer");

      // Decode JWT token to get user info
      const decoded = jwtDecode(action.payload.token);
      if (decoded) {
        state.user = JSON.stringify(decoded);
        localStorage.setItem("user", JSON.stringify(decoded));
        localStorage.setItem("userName", decoded.displayname || decoded.name);
        localStorage.setItem("userId", decoded.employeeid || decoded.id);
        localStorage.setItem("roleId", decoded.roleid || decoded.role);
        localStorage.setItem(
          "EmployeeNumber",
          decoded.EmployeeNumber || decoded.EmployeeNumber
        );
      }

      // Check if biometric was previously enabled
      state.biometricEnabled =
        localStorage.getItem("biometricEnabled") === "true";
    },
    loginFailure(state, action) {
      state.loader = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },

    // Biometric login flow
    biometricLogin(state) {
      state.biometricLoader = true;
      state.error = null;
    },
    biometricLoginSuccess(state, action) {
      state.biometricLoader = false;
      state.isAuthenticated = !!action.payload.token;
      state.token = JSON.stringify(action.payload.token);
      state.tokenExpired = false;

      // Store in localStorage
      localStorage.setItem("isAuthenticated", state.isAuthenticated);
      localStorage.setItem("token", state.token);
      localStorage.setItem("token_bearer", action.payload.token);
      localStorage.setItem("token_type", action.payload.token_Type || "Bearer");

      // Decode JWT token
      const decoded = jwtDecode(action.payload.token);
      if (decoded) {
        state.user = JSON.stringify(decoded);
        localStorage.setItem("user", JSON.stringify(decoded));
        localStorage.setItem("userName", decoded.displayname || decoded.name);
        localStorage.setItem("userId", decoded.employeeid || decoded.id);
        localStorage.setItem("roleId", decoded.roleid || decoded.role);
      }
    },
    biometricLoginFailure(state, action) {
      state.biometricLoader = false;
      state.error = action.payload;
    },

    // Enable biometric authentication
    enableBiometric(state, action) {
      console.log("Hitting enableBiometric slice");
      state.biometricEnabled = true;
      localStorage.setItem("biometricEnabled", "true");
      if (action.payload.encryptedCredentials) {
        localStorage.setItem(
          "biometricCredentials",
          action.payload.encryptedCredentials
        );
      }
    },

    // Session management - called on app start
    loginSessionAvailable(state) {
      const token = localStorage.getItem("token_bearer");
      const tokenType = localStorage.getItem("token_type");

      if (!token || !tokenType) {
        // No token found - user not authenticated
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.tokenExpired = false;
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        if (decoded.exp && decoded.exp < currentTime) {
          // Token expired
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          state.tokenExpired = true;
          localStorage.clear();
        } else {
          // Token is valid
          state.isAuthenticated = true;
          state.token = JSON.stringify(token);
          state.user = JSON.stringify(decoded);
          state.tokenExpired = false;
          state.biometricEnabled =
            localStorage.getItem("biometricEnabled") === "true";
        }
      } catch (error) {
        // Invalid token
        console.error("Invalid token:", error);
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.tokenExpired = true;
        localStorage.clear();
      }
    },

    // Handle unauthorized API responses
    handleUnauthorized(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.tokenExpired = true;
      state.biometricEnabled = false;
      localStorage.clear();
    },

    // Token expiration actions
    tokenExpired(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.tokenExpired = true;
      state.biometricEnabled = false;
      localStorage.clear();
    },

    resetTokenExpired(state) {
      state.tokenExpired = false;
    },

    setLanguage(state, action) {
      state.language = action.payload;
    },
    setBiometricSupported(state, action) {
      state.biometricSupported = action.payload;
      if (action.payload) {
        state.biometricEnabled =
          localStorage.getItem("biometricEnabled") === "true";
      }
    },
    clearError(state) {
      state.error = null;
    },
    setBiometricEnabled: (state, action) => {
      state.biometricEnabled = action.payload;
    },

    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.biometricEnabled = false;
      state.firstTimeLogin = false;
      state.error = null;
      state.tokenExpired = false;
      state.loader = false;
      state.biometricLoader = false;

      // localStorage.clear();
    },
  },
});

export const {
  login,
  loginSuccess,
  loginFailure,
  biometricLogin,
  biometricLoginSuccess,
  biometricLoginFailure,
  enableBiometric,
  loginSessionAvailable,
  handleUnauthorized,
  tokenExpired,
  resetTokenExpired,
  setLanguage,
  setBiometricSupported,
  clearError,
  logout,
  setBiometricEnabled,
} = authSlice.actions;

export default authSlice.reducer;
